import { pdf } from "@react-pdf/renderer";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { PDFDocument } from "pdf-lib";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import type { FolderObject } from "components/folder-picker/folder-picker.component";
import {
  PdfMeta,
  usePDFGenerationV3
} from "components/pdf-generation-v3/pdf-generation-v3.hook";
import type { MergeTagExtraData } from "components/word-processor-field/merge-tags/merge-tags-values/merge-tags-values";
import { setAiPromptDetails } from "components/word-processor-field/word-processor.slice";
import { VERTICAL_SOUTHWESTERN_LIMITATIONS } from "constants/feature-flags";
import { PROMPT_TYPE_TEXT_TEMPLATE } from "constants/prompts";
import {
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import { PATIENT_HEADER_QUERY } from "features/patients/use-patient.hook";
import useClinicNotifications from "hooks/use-clinic-notifications";
import { ensureHtmlLineBreaks } from "utils/format-compat";
import { useGetClinicQuery } from "services/clinics";
import {
  useGetBoxAccessTokenQuery,
  useUploadDocumentMutation
} from "services/e-documents";
import { useGetPatientQuery } from "services/patients";
import { useGetStaffMemberQuery } from "services/staff";
import { useAutoCompleteTextTemplatesMutation } from "services/text-templates";
import type { FormTemplate } from "services/types/form-templates.type";
import type { Patient } from "services/types/patients.type";
import type { MinimalStaffMember } from "services/types/staff.type";
import { openFile } from "utils/common";

import MemoizedCustomPrintFormPdfRender from "./custom-print-form-pdf-render.component";

type AskAiParams = {
  id: string;
  type: number;
  name: string;
  prompt: string;
  fieldContent: string;
  lastResult?: string;
};

type CustomPrintFormPreviewProviderProps = {
  children?: React.ReactNode;
  aiFeatureEnabled?: boolean;
  providerSignatureEnabled?: boolean;
  enableHeaderDefaultValue?: boolean;
  enablePatientSignatureDefaultValue?: boolean;
  enableProviderSignatureDefaultValue?: boolean;
};

type CustomPrintFormPreviewContextType = {
  formContent: string;
  setFormContent: (formContent: string) => void;
  pdfMeta: PdfMeta | undefined;
  setPdfMeta: React.Dispatch<React.SetStateAction<PdfMeta | undefined>>;
  patientId: number | undefined;
  setPatientId: React.Dispatch<React.SetStateAction<number | undefined>>;
  showDialog: () => void;
  hideDialog: () => void;
  dialogOpened: boolean;
  handleAskAi: (params: AskAiParams) => Promise<void> | undefined;
  isTemplateCompleting: boolean;
  autocompleteTextTemplate: (text: string) => void;
  handlePrint: (content: string) => void;
  patient?: Patient;
  metadata: CustomPrintFormMetadata;
  setMetadata: React.Dispatch<React.SetStateAction<CustomPrintFormMetadata>>;
  isReady: boolean;
  enableHeader: boolean;
  setEnableHeader: React.Dispatch<React.SetStateAction<boolean>>;
  setTemplate: React.Dispatch<
    React.SetStateAction<FormTemplate | null | undefined>
  >;
  enablePatientSignature: boolean;
  setEnablePatientSignature: React.Dispatch<React.SetStateAction<boolean>>;
  enableProviderSignature: boolean;
  setEnableProviderSignature: React.Dispatch<React.SetStateAction<boolean>>;
  //
  pdfFormBlobUrl: string | undefined;
  showSignModal: boolean;
  setShowSignModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmitSignature: (
    files: File[],
    uploadToEdocs?: boolean
  ) => Promise<void> | void;
  signature: File | undefined;
  handleSelectBoxFolder: (selectedFolder: FolderObject | null) => Promise<void>;
  folderPickerOpen: boolean;
  setFolderPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isEdocsAvailable: boolean;
  boxFolderId?: string;
  boxAccessToken?: string | null;
  handleSaveToEdocs: () => void;
  isGeneratingPdfForEdocs: boolean;
  currentTab: string;
  setCurrentTab: React.Dispatch<React.SetStateAction<string>>;
  handleRegeneratePdf: () => void;
  handleShowSignModal: () => Promise<void>;
  aiFeatureEnabled?: boolean;
  providerSignatureEnabled?: boolean;
  setSignature: React.Dispatch<React.SetStateAction<File | undefined>>;
  wordProcessorFieldId: string;
  setWordProcessorFieldId: React.Dispatch<React.SetStateAction<string>>;
  nativePdfTablesEnabled: boolean;
  setNativePdfTablesEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  numberOfPages: number | null;
  recalculateNumberOfPagesWithContent: (content: string) => Promise<void>;
  footerSignerId: number | null;
  setFooterSignerId: React.Dispatch<React.SetStateAction<number | null>>;
  footerSignatureDataUrl: string | null;
  setFooterSignatureDataUrl: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  showFooterSignModal: boolean;
  setShowFooterSignModal: React.Dispatch<React.SetStateAction<boolean>>;
  effectiveClinicId: number | undefined;
};

const CustomPrintFormPreviewContext =
  createContext<CustomPrintFormPreviewContextType | null>(null);

type CustomPrintFormMetadata = {
  signedAt?: string | null;
  dateOfVisit?: string | null;
  staffMember?: MinimalStaffMember | null;
  clinicId?: number;
  fileNamePostfix?: string;
  additionalMergeTagsValues?: MergeTagExtraData;
};

export function CustomPrintFormPreviewProvider({
  children,
  aiFeatureEnabled,
  providerSignatureEnabled,
  enableHeaderDefaultValue = true,
  enablePatientSignatureDefaultValue = true,
  enableProviderSignatureDefaultValue = true
}: CustomPrintFormPreviewProviderProps) {
  const flags = useFlags();
  const verticalSouthwesternLimitationsEnabled =
    flags[VERTICAL_SOUTHWESTERN_LIMITATIONS];
  const isPdfNativeTablesEnabled = Boolean(
    useAppSelector(selectCurrentUser)?.native_pdf_tables_enabled ||
      verticalSouthwesternLimitationsEnabled
  );

  const dispatch = useAppDispatch();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const uploadBlobRef = useRef<Blob | undefined>();
  const [pdfFormBlobUrl, setPdfFormBlobUrl] = useState<string | undefined>();
  const [numberOfPages, setNumberOfPages] = useState<number | null>(null);
  const [isBusy, setIsBusy] = useState<boolean>(false);

  const clinic_id = currentClinic?.id ? Number(currentClinic.id) : undefined;

  const [signature, setSignature] = useState<File | undefined>();
  const [footerSignerId, setFooterSignerId] = useState<number | null>(null);
  const [showSignModal, setShowSignModal] = useState(false);
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);

  const { data: boxData } = useGetBoxAccessTokenQuery();

  const [metadata, setMetadata] = useState<CustomPrintFormMetadata>({});

  const { data: staffMember, isFetching: staffMemberFetching } =
    useGetStaffMemberQuery(Number(metadata.staffMember?.id), {
      skip: !metadata.staffMember?.id
    });

  const { data: footerSignerStaffMember, isFetching: footerSignerFetching } =
    useGetStaffMemberQuery(Number(footerSignerId), {
      skip: !footerSignerId
    });

  const effectiveClinicId = metadata.clinicId || clinic_id;

  const [initialized, setInitialized] = useState(false);
  const [patientId, setPatientId] = useState<number | undefined>();
  const lastPracticeIdRef = useRef<number | undefined>(undefined);

  const [formContent, setFormContent] = useState("");
  const [triggerPrint, setTriggerPrint] = useState(false);
  const [triggerGeneratePdf, setTriggerGeneratePdf] = useState(false);
  const [triggerSaveToEdocs, setTriggerSaveToEdocs] = useState(false);
  const [isGeneratingPdfForEdocs, setIsGeneratingPdfForEdocs] = useState(false);
  const [nativePdfTablesEnabled, setNativePdfTablesEnabled] = useState(
    isPdfNativeTablesEnabled
  );
  const [enableHeader, setEnableHeader] = useState(enableHeaderDefaultValue);
  const [enablePatientSignature, setEnablePatientSignature] = useState(
    enablePatientSignatureDefaultValue
  );
  const [enableProviderSignature, setEnableProviderSignature] = useState(
    providerSignatureEnabled ? enableProviderSignatureDefaultValue : false
  );

  const [footerSignatureDataUrl, setFooterSignatureDataUrl] = useState<
    string | null
  >(null);
  const [showFooterSignModal, setShowFooterSignModal] = useState(false);

  const [wordProcessorFieldId, setWordProcessorFieldId] = useState("");
  const [currentTab, setCurrentTab] = useState("form");

  const [template, setTemplate] = useState<FormTemplate | null | undefined>(
    undefined
  );

  const [showPrintableFormDialog, setShowPrintableFormDialog] = useState(false);

  const { pdfMeta, setPdfMeta } = usePDFGenerationV3();

  const [autocompleteTemplate, { isLoading: isTemplateCompleting }] =
    useAutoCompleteTextTemplatesMutation();

  const { clearCompletionChunks } = useClinicNotifications();

  const [uploadDocument, { isLoading: isUploadingToEDocs }] =
    useUploadDocumentMutation();

  useEffect(() => {
    setNativePdfTablesEnabled(isPdfNativeTablesEnabled);
  }, [isPdfNativeTablesEnabled]);

  const { data: patient, isFetching: isPatientFetching } = useGetPatientQuery(
    { patientId: Number(patientId), query: PATIENT_HEADER_QUERY },
    {
      skip: !patientId || patientId === -1 || !template
    }
  );

  // Latch the Box access token to its first truthy value while the folder
  // picker is open, then reset it on close. The picker refreshes its own
  // token internally right before calling Box's show(), and that refresh
  // updates the shared getBoxAccessToken cache. Reading the live cache value
  // here would feed that churn back into the picker's mount gate and token
  // prop, producing a token-refetch/remount loop, so we hold a stable token
  // for as long as the dialog stays open.
  const [stableBoxToken, setStableBoxToken] = useState<string | null>(null);

  useEffect(() => {
    if (!folderPickerOpen) {
      setStableBoxToken(null);
      return;
    }
    setStableBoxToken((prev) => prev ?? boxData?.box_access_token ?? null);
  }, [folderPickerOpen, boxData?.box_access_token]);

  const isEdocsAvailable = Boolean(patient?.box_folder_id && stableBoxToken);

  const { data: printClinic, isFetching: isClinicFetching } = useGetClinicQuery(
    { clinicId: Number(metadata.clinicId || clinic_id) },
    {
      skip: !(metadata.clinicId || clinic_id) || !template,
      refetchOnMountOrArgChange: true
    }
  );

  const handleAskAi = useCallback(
    (params: AskAiParams) => {
      const { id, type, name, prompt, fieldContent, lastResult } = params;
      clearCompletionChunks();
      dispatch(
        setAiPromptDetails({
          id,
          prompt,
          result: lastResult,
          dialogOpen: true,
          isBusy: true
        })
      );

      return autocompleteTemplate({
        name,
        text: fieldContent,
        user_prompt: prompt,
        type,
        clinic_id,
        format: "html"
      }).then(({ data }: any) => {
        clearCompletionChunks();
        dispatch(
          setAiPromptDetails({
            id,
            prompt,
            result: ensureHtmlLineBreaks(data),
            dialogOpen: true,
            isBusy: false
          })
        );
      });
    },
    [autocompleteTemplate, clearCompletionChunks, clinic_id, dispatch]
  );

  const fileName = patient
    ? `${patient?.first_name} ${patient?.last_name}${metadata.fileNamePostfix ? "_" + metadata.fileNamePostfix : ""}.pdf`
    : "form.pdf";

  const autocompleteTextTemplate = useCallback(
    (text: string) => {
      setCurrentTab("form");
      clearCompletionChunks();

      dispatch(
        setAiPromptDetails({
          id: wordProcessorFieldId,
          prompt: "",
          dialogOpen: true,
          isBusy: true
        })
      );
      autocompleteTemplate({
        name: "Appointment printable form",
        text,
        type: PROMPT_TYPE_TEXT_TEMPLATE,
        clinic_id,
        format: "html"
      }).then(({ data }: any) => {
        dispatch(
          setAiPromptDetails({
            id: wordProcessorFieldId,
            result: ensureHtmlLineBreaks(data),
            isBusy: false
          })
        );
      });
    },
    [
      wordProcessorFieldId,
      autocompleteTemplate,
      clearCompletionChunks,
      clinic_id,
      dispatch
    ]
  );

  const handleHidePrintableFormDialog = useCallback(() => {
    setShowPrintableFormDialog(false);
    setTriggerPrint(false);
    setCurrentTab("form");
    setFooterSignerId(null);
    setFooterSignatureDataUrl(null);
  }, []);

  const handleShowPrintableFormDialog = useCallback(() => {
    setShowPrintableFormDialog(true);
    setTriggerPrint(false);
  }, []);

  const staffMemberSignature = staffMember?.signature;

  const effectiveStaffMember =
    footerSignerId && footerSignerStaffMember
      ? (footerSignerStaffMember as MinimalStaffMember)
      : (staffMember as MinimalStaffMember);
  const effectiveSignerSignature =
    footerSignatureDataUrl ??
    (footerSignerId && footerSignerStaffMember
      ? footerSignerStaffMember.signature
      : staffMemberSignature);

  const updateNumberOfPages = useCallback(async (blob: Blob) => {
    const pdfDoc = await PDFDocument.load(await blob.arrayBuffer());
    const pageCount = pdfDoc.getPageCount();
    setNumberOfPages(pageCount);
  }, []);

  const pdfDocument = useMemo(() => {
    return (
      <MemoizedCustomPrintFormPdfRender
        flags={flags}
        content={formContent}
        isPdfNativeTablesEnabled={
          nativePdfTablesEnabled || verticalSouthwesternLimitationsEnabled
        }
        staffMemberSignature={effectiveSignerSignature}
        patient={patient}
        clinic={printClinic}
        fileName={fileName}
        enableHeader={enableHeader || verticalSouthwesternLimitationsEnabled}
        enablePatientSignature={enablePatientSignature}
        enableProviderSignature={enableProviderSignature}
        signature={signature}
        signedAt={metadata.signedAt}
        dateOfVisit={metadata.dateOfVisit}
        staffMember={effectiveStaffMember}
      />
    );
  }, [
    formContent,
    effectiveSignerSignature,
    patient,
    printClinic,
    fileName,
    enableHeader,
    enablePatientSignature,
    enableProviderSignature,
    signature,
    metadata.signedAt,
    metadata.dateOfVisit,
    effectiveStaffMember,
    nativePdfTablesEnabled,
    flags,
    verticalSouthwesternLimitationsEnabled
  ]);

  const recalculateNumberOfPagesWithContent = useCallback(
    async (content: string) => {
      const tempPdfDocument = (
        <MemoizedCustomPrintFormPdfRender
          flags={flags}
          content={content}
          isPdfNativeTablesEnabled={
            nativePdfTablesEnabled || verticalSouthwesternLimitationsEnabled
          }
          staffMemberSignature={effectiveSignerSignature}
          patient={patient}
          clinic={printClinic}
          fileName={fileName}
          enableHeader={enableHeader || verticalSouthwesternLimitationsEnabled}
          enablePatientSignature={enablePatientSignature}
          enableProviderSignature={enableProviderSignature}
          signature={signature}
          signedAt={metadata.signedAt}
          dateOfVisit={metadata.dateOfVisit}
          staffMember={effectiveStaffMember}
        />
      );
      const blob = await pdf(tempPdfDocument).toBlob();
      updateNumberOfPages(blob);
    },
    [
      flags,
      nativePdfTablesEnabled,
      effectiveSignerSignature,
      patient,
      printClinic,
      fileName,
      enableHeader,
      enablePatientSignature,
      enableProviderSignature,
      signature,
      metadata.signedAt,
      metadata.dateOfVisit,
      effectiveStaffMember,
      updateNumberOfPages,
      verticalSouthwesternLimitationsEnabled
    ]
  );

  const handlePrint = useCallback(async (content: string) => {
    setFormContent(content);
    setTriggerPrint(true);
  }, []);

  const handleRegeneratePdf = useCallback(async () => {
    setTriggerGeneratePdf(true);
  }, []);

  const handleTriggerRegeneratePdf = useCallback(async () => {
    setIsBusy(true);

    const blob = await pdf(pdfDocument).toBlob();
    uploadBlobRef.current = blob;

    updateNumberOfPages(blob);

    if (pdfFormBlobUrl) {
      URL.revokeObjectURL(pdfFormBlobUrl);
    }
    const url = URL.createObjectURL(blob);
    setPdfFormBlobUrl(url);

    setTimeout(() => {
      setIsBusy(false);
    }, 1);
    setTriggerGeneratePdf(false);

    return url;
  }, [pdfDocument, pdfFormBlobUrl, updateNumberOfPages]);

  useEffect(() => {
    if (triggerGeneratePdf) {
      handleTriggerRegeneratePdf();
    }
  }, [handleTriggerRegeneratePdf, triggerGeneratePdf]);

  const handleTriggerPrint = useCallback(async () => {
    setTriggerPrint(false);
    const url = await handleTriggerRegeneratePdf();
    setTimeout(() => {
      openFile(String(url), true);
    }, 1);
  }, [handleTriggerRegeneratePdf]);

  useEffect(() => {
    if (triggerPrint) {
      handleTriggerPrint();
    }
  }, [handleTriggerPrint, triggerPrint]);

  const handleSaveToEdocs = useCallback(() => {
    // Use trigger pattern to ensure PDF is generated in next render cycle
    // after all state updates (including signature) have been applied
    setIsGeneratingPdfForEdocs(true);
    setTriggerSaveToEdocs(true);
  }, []);

  const handleTriggerSaveToEdocs = useCallback(() => {
    setTriggerSaveToEdocs(false);
    // Use setTimeout to let the UI update and show loading state before heavy PDF generation
    setTimeout(async () => {
      try {
        // By this point, pdfDocument memo has been recalculated with the latest signature
        const blob = await pdf(pdfDocument).toBlob();
        uploadBlobRef.current = blob;
        setFolderPickerOpen(true);
      } finally {
        setIsGeneratingPdfForEdocs(false);
      }
    }, 0);
  }, [pdfDocument]);

  useEffect(() => {
    if (triggerSaveToEdocs) {
      handleTriggerSaveToEdocs();
    }
  }, [handleTriggerSaveToEdocs, triggerSaveToEdocs]);

  const handleSubmitSignature = useCallback(
    (files: File[], uploadToEdocs?: boolean) => {
      const signature = files?.at(0);
      setSignature(signature);

      if (uploadToEdocs) {
        // Use trigger pattern - PDF will be generated in next render cycle
        // after signature state has been applied to the pdfDocument memo
        setIsGeneratingPdfForEdocs(true);
        setTriggerSaveToEdocs(true);
      } else {
        // Regenerate PDF preview to show the signature
        handleRegeneratePdf();
      }
    },
    [handleRegeneratePdf]
  );

  const handleSelectBoxFolder = useCallback(
    async (selectedFolder: FolderObject | null) => {
      const formData = new FormData();

      const blob = uploadBlobRef.current;

      if (patient && blob) {
        formData.append("document", blob, fileName);
        formData.append(
          "destination_folder_id",
          String(selectedFolder?.id || patient.box_folder_id)
        );

        uploadDocument({
          id: patient.id,
          type: "patients" as const,
          formData: formData
        }).then((response) => {
          if (response && "error" in response) {
            dispatch(
              setCustomMessage(<ErrorSnackbarContent response={response} />)
            );
            dispatch(setOpen(true));
          } else {
            dispatch(
              setCustomMessage("PDF File Was Uploaded to Selected Folder")
            );
            dispatch(setOpen(true));
          }
        });
      }
    },
    [dispatch, fileName, patient, uploadDocument]
  );

  const handleShowSignModal = useCallback(async () => {
    await handleTriggerRegeneratePdf();
    setShowSignModal(true);
  }, [handleTriggerRegeneratePdf]);

  const staffMemberLoading = metadata.staffMember?.id
    ? staffMemberFetching
    : false;
  const footerSignerLoading = footerSignerId ? footerSignerFetching : false;

  const isInitialized = Boolean(
    !staffMemberLoading &&
      patient &&
      printClinic &&
      template &&
      !isPatientFetching &&
      !isClinicFetching
  );

  const isReady = Boolean(
    isInitialized && !isUploadingToEDocs && !footerSignerLoading
  );

  useEffect(() => {
    if (!isInitialized) return;

    const practiceId = printClinic?.practice.id;
    // On first mount lastPracticeIdRef.current is undefined, so practiceChanged is always
    // false — the empty initial state comes from useState(""), not this effect.
    const practiceChanged =
      lastPracticeIdRef.current !== undefined &&
      lastPracticeIdRef.current !== practiceId;
    lastPracticeIdRef.current = practiceId;

    if (practiceChanged) {
      setFormContent("");
    }
    setEnableHeader(enableHeaderDefaultValue);
    setEnablePatientSignature(enablePatientSignatureDefaultValue);
    setEnableProviderSignature(
      providerSignatureEnabled ? enableProviderSignatureDefaultValue : false
    );
    setSignature(undefined);
    setFooterSignerId(null);
    setFooterSignatureDataUrl(null);
    setInitialized(true);
  }, [
    enableHeaderDefaultValue,
    enablePatientSignatureDefaultValue,
    enableProviderSignatureDefaultValue,
    isInitialized,
    printClinic?.practice.id,
    providerSignatureEnabled,
    template?.id
  ]);

  const value = useMemo(() => {
    return {
      formContent,
      setFormContent,
      pdfMeta,
      setPdfMeta,
      patientId,
      setPatientId,
      hideDialog: handleHidePrintableFormDialog,
      showDialog: handleShowPrintableFormDialog,
      dialogOpened: showPrintableFormDialog,
      handleAskAi,
      isTemplateCompleting,
      autocompleteTextTemplate,
      handlePrint,
      patient,
      metadata,
      setMetadata,
      isReady: isReady && !isBusy,
      enableHeader,
      setEnableHeader,
      setTemplate,
      enablePatientSignature,
      setEnablePatientSignature,
      enableProviderSignature,
      setEnableProviderSignature,
      pdfFormBlobUrl,
      showSignModal,
      setShowSignModal,
      handleSubmitSignature,
      signature,
      handleSelectBoxFolder,
      folderPickerOpen,
      setFolderPickerOpen,
      isEdocsAvailable,
      boxFolderId: patient?.box_folder_id,
      boxAccessToken: stableBoxToken,
      handleSaveToEdocs,
      isGeneratingPdfForEdocs,
      currentTab,
      setCurrentTab,
      handleShowSignModal,
      handleRegeneratePdf,
      initialized,
      aiFeatureEnabled,
      providerSignatureEnabled,
      setSignature,
      wordProcessorFieldId,
      setWordProcessorFieldId,
      nativePdfTablesEnabled,
      setNativePdfTablesEnabled,
      numberOfPages,
      recalculateNumberOfPagesWithContent,
      footerSignerId,
      setFooterSignerId,
      footerSignatureDataUrl,
      setFooterSignatureDataUrl,
      showFooterSignModal,
      setShowFooterSignModal,
      effectiveClinicId
    };
  }, [
    formContent,
    pdfMeta,
    setPdfMeta,
    patientId,
    handleHidePrintableFormDialog,
    handleShowPrintableFormDialog,
    showPrintableFormDialog,
    wordProcessorFieldId,
    handleAskAi,
    isTemplateCompleting,
    autocompleteTextTemplate,
    handlePrint,
    patient,
    metadata,
    isReady,
    isBusy,
    enableHeader,
    enablePatientSignature,
    enableProviderSignature,
    pdfFormBlobUrl,
    showSignModal,
    handleSubmitSignature,
    signature,
    handleSelectBoxFolder,
    folderPickerOpen,
    isEdocsAvailable,
    stableBoxToken,
    handleSaveToEdocs,
    isGeneratingPdfForEdocs,
    currentTab,
    handleShowSignModal,
    handleRegeneratePdf,
    initialized,
    aiFeatureEnabled,
    providerSignatureEnabled,
    nativePdfTablesEnabled,
    setNativePdfTablesEnabled,
    numberOfPages,
    recalculateNumberOfPagesWithContent,
    footerSignerId,
    footerSignatureDataUrl,
    showFooterSignModal,
    effectiveClinicId
  ]);

  return (
    <CustomPrintFormPreviewContext.Provider value={value}>
      {children}
    </CustomPrintFormPreviewContext.Provider>
  );
}

export default function useCustomPrintFormPreview() {
  const context = useContext(CustomPrintFormPreviewContext);
  if (!context) {
    throw new Error(
      `useCustomPrintFormPreview must be used within an CustomPrintFormPreviewProvider`
    );
  }

  return context;
}
